<script setup lang="ts">
import type { MetricOptions, SeriePercent, SeriePercentValue } from '@/types/charts.types'

defineOptions({
  name: 'PercentBarMetric',
})

const props = withDefaults(defineProps<Props>(), {
  data: () => [],
  options: () => ({}),
  legend: true,
  limit: 5,
  minWidth: 10,
  height: 'h-4',
})

interface Props {
  data?: SeriePercent[]
  options?: MetricOptions<'bar'>
  legend?: boolean
  limit?: number
  minWidth?: number
  height?: string // TailwindCSS height class
}

const { t } = useI18n()
const isOpen = ref(false)

const series = computed((): SeriePercent[] | null => {
  const { data, options } = props

  return data?.map(
    (d, dataIndex) => {
      const label = options.label ? options.label(d) : d.label

      return {
        ...d,
        name: label,
        value: 'value' in d ? d.value : undefined,
        percent: d.percent,
        color: (options.color && options.color(d)) || d.color || getColorByIndex(dataIndex),
      }
    },
  )
})

const limitedSeries = computed((): SeriePercent[] | null => {
  const { limit } = props
  const seriesRef = series.value

  if (!seriesRef) {
    return null
  }

  if (limit && seriesRef.length > limit) {
    const limited = seriesRef.slice(0, limit)

    limited?.push({
      name: t('Other'),
      value: seriesRef.slice(limit)
        .filter((s): s is SeriePercentValue => 'value' in s && s.value != null)
        .reduce((total, { value }) => total + (value as number), 0) || undefined,
      percent: seriesRef.slice(limit)
        .reduce((total, { percent }) => total + percent, 0) ?? 0,
      color: getColorByIndex(limit),
    })

    return limited || null
  } else {
    return seriesRef || null
  }
})
</script>

<template>
  <div class="percent-bar-metric">
    <div class="flex relative w-full">
      <div
        v-if="limitedSeries === null"
        class="w-full text-center text-gray-400 text-sm"
        :class="[height]"
      >
        {{ t('N/A') }}
      </div>
      <template v-else-if="limitedSeries?.length > 0">
        <DTooltip
          v-for="serie in limitedSeries"
          :key="serie.name"
          :class="[height]"
          :style="{
            backgroundColor: serie.color,
            width: `${serie.percent}%`,
            minWidth: serie.percent > 0 ? `${minWidth}px` : null,
          }"
        >
          <div />
          <template #title>
            <div
              class="h-4 w-4 rounded-full inline-block align-middle mr-1"
              :style="{ backgroundColor: serie.color as string }"
            />
            <strong>{{ serie.name }}:</strong>
          </template>
          <template #content>
            <slot
              name="tooltip"
              v-bind="serie"
            >
              <p
                v-if="'value' in serie && serie.value != null"
                class="whitespace-nowrap"
              >
                {{ `${formatNumber(serie.value)} (${formatPercent(serie.percent)})` }}
              </p>
              <p
                v-else
                class="whitespace-nowrap"
              >
                {{ `${formatPercent(serie.percent)}` }}
              </p>
            </slot>
          </template>
        </DTooltip>
      </template>

      <div
        v-else
        class="w-full bg-grey-100"
        :class="[height]"
      />
    </div>

    <div
      v-if="legend && series"
      class="pt-4"
    >
      <ChartLegend
        v-if="limitedSeries"
        class="flex-col"
        :series="limitedSeries"
        :dimension="options?.dimension"
        :sub-legend="
          (d) => ('percent' in d ? ` | ${formatPercent(d.percent)}` : null)
        "
        :selectable="false"
      />
      <DLink
        v-if="limit && series.length > limit"
        class="text-blue-500 underline text-sm ml-6"
        @click="() => (isOpen = true)"
      >
        {{ t('See all') }}
      </DLink>

      <DModal
        v-if="isOpen"
        @close="() => (isOpen = false)"
      >
        <template #title>
          {{ t('Details on cars models') }}
        </template>

        <div class="max-h-60 overflow-auto">
          <ul
            v-for="item in series"
            :key="item.name"
          >
            <li>
              {{ item.name }}
              <span class="font-bold text-grey-400 float-right">{{
                formatPercent(item.percent)
              }}</span>
            </li>
          </ul>
        </div>
      </DModal>
    </div>
  </div>
</template>
